<template>
  <b-layout :colors="$site.colors" :header-title="$t('pages.gift.card.index.title')" :header-back-route="homeRoute" :breadcrumbs="breadcrumbs">
    <b-container class="mt-12">
      <b-summary>
        <template #summary>
          <b-card class="text-secondary">
            <img v-if="giftCardImage" :src="giftCardImage" class="mb-4 rounded">

            <div v-if="details.toName" class="mb-1">
              <strong>{{ details.toName }},</strong>
            </div>
            {{ details.message }}
            <div v-if="details.fromName" class="mt-3">
              <strong>{{ details.fromName }}</strong>
            </div>
            <hr>
            <h3 v-if="details.amount" class="font-semibold text-xl mt-6 mb-6">
              {{ $site.location.currencySymbol }}{{ currencyAmount(details.amount) }}
            </h3>
            <h6 class="text-xs font-weight-light mb-2">
              {{ $t('pages.gift.card.index.locationGiftCard',{location: $site.location.title}) }}
            </h6>

            <small class="text-xs">
              <strong>{{ $t('general.expires') }}:</strong>  {{ giftCardExpiryDateLabel }}
            </small>
          </b-card>
        </template>

        <div>
          <b-errors ref="errors" :errors="errors" />
          <b-card :title="$t('pages.gift.card.index.giftCardDetails')" class="mb-4">
            <UFormGroup v-if="images && images.length > 1" :label="$t('pages.gift.card.index.design')+':'" class="mb-4">
              <div class="flex flex-wrap">
                <a
                  v-for="image in images"
                  :key="image.id"
                  class="w-32 h-24 rounded mb-2 mr-2 overflow-hidden cursor-pointer hover:scale-105 transition-all"
                  :class="{ 'outline outline-offset-2 outline-2 outline-action' : (details.image && details.image.id == image.id) }"
                  @click="setImage(image)"
                >
                  <div :style="{ backgroundImage: `url(${image.images.medium})` }" class="bg-cover bg-center h-full w-full" />
                </a>
              </div>
            </UFormGroup>

            <UFormGroup :label="$t('pages.gift.card.index.amount')+':'" class="mb-6">
              <template v-if="amounts && amounts.length > 0">
                <template v-if="amounts">
                  <b-button
                    v-for="(amount, index) in amounts"
                    :key="'amount-' + index"
                    variant="action"
                    :flat="details.amount !== amount"
                    class="mb-1 mr-1"
                    @click="setAmount(amount)"
                  >
                    {{ $site.location.currencySymbol }}{{ currencyAmount(amount) }}
                  </b-button>

                  <b-button
                    v-if="$site.tabs.giftCard.allowCustomAmount"
                    variant="action"
                    :flat="!_customAmount"
                    class="mb-1 mr-1"
                    @click="setAmount('0')"
                  >
                    {{ $t('pages.gift.card.index.customAmount') }}
                  </b-button>
                </template>

                <UFormGroup v-if="_customAmount && $site.tabs.giftCard.allowCustomAmount" :label="$t('pages.gift.card.index.customAmount')" size="lg" class="mt-4 mb-4">
                  <UInput v-model="details.amount" size="lg">
                    <template #leading>
                      <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary">
                        {{ $site.location.currencySymbol }}
                      </p>
                    </template>
                  </UInput>
                </UFormGroup>
              </template>

              <template v-else>
                <UInput v-if="$site.tabs.giftCard.allowCustomAmount || !amounts" v-model="details.amount" size="lg">
                  <template #leading>
                    <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary">
                      {{ $site.location.currencySymbol }}
                    </p>
                  </template>
                </UInput>
              </template>
            </UFormGroup>

            <UFormGroup :label="$t('pages.gift.card.index.to')+':'" class="mb-6">
              <UFormGroup size="sm" class="mb-1">
                <UInput v-model="details.toName" :placeholder="$t('pages.gift.card.index.namePlaceholder')" size="lg" class="mb-4" :ui="{ leading: { padding: { lg: 'ps-20'}}}">
                  <template #leading>
                    <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary">
                      {{ $t('general.name') }}
                    </p>
                  </template>
                </UInput>
              </UFormGroup>

              <UFormGroup size="sm">
                <UInput v-model.trim="details.toEmail" :placeholder="$t('pages.gift.card.index.emailPlaceholder')" size="lg" :ui="{ leading: { padding: { lg: 'ps-20'}}}">
                  <template #leading>
                    <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary">
                      {{ $t('general.email') }}
                    </p>
                  </template>
                </UInput>
              </UFormGroup>
            </UFormGroup>

            <UFormGroup :label="$t('pages.gift.card.index.from')+':'" class="mb-4">
              <UInput v-model="details.fromName" :placeholder="$t('pages.gift.card.index.fromPlaceholder')" size="lg" />
            </UFormGroup>

            <UFormGroup :label="$t('pages.gift.card.index.message')+':'" class="mb-4">
              <UTextarea
                v-model="details.message"
                :placeholder="$t('pages.gift.card.index.messagePlaceholder')"
                :rows="3"
                max-rows="6"
                flat
              />
            </UFormGroup>

            <UFormGroup :label="$t('pages.gift.card.index.deliveryDate')+':'">
              <client-only>
                <FieldsDate
                  v-model="details.deliveryDate"
                  :min-date="new Date()"
                  :max-date="new Date(new Date().getFullYear() + 1, 11, 31)"
                  :placeholder="$t('pages.gift.card.index.deliveryDatePlaceholder')" size="lg"
                  augmenter="ymd"
                />
              </client-only>
            </UFormGroup>
          </b-card>
          <b-button variant="action" class="w-full" @click="next">
            {{ $t('pages.gift.card.index.buyGiftCardAmount',{amount: $site.location.currencySymbol + (currencyAmount(details.amount))}) }} »
          </b-button>
        </div>
      </b-summary>
    </b-container>
  </b-layout>
</template>

<script>
import DateField from '@/components/fields/Date'
import Utils from '@/utils'

definePageMeta({
  tabs: ['giftCard'],
  middleware: ['redirects'],
  layout: 'default',
});

export default defineNuxtComponent({
  components: {
    DateField
  },
  data () {
    return {
      errors: [],
      details: {
        toName: '',
        toEmail: '',
        fromName: '',
        amount: '',
        message: this.$t('pages.gift.card.index.enjoy'),
        image: null,
        deliveryDate: null
      }
    }
  },
  computed: {
    _customAmount: {
      get () {
        return !this.amounts?.includes(this.details.amount) || !this.amounts?.length
      },
      set (value) {
        if (value) {
          this.details.amount = ''
        }
      }
    },
    title () {
      return this.$site?.tabs?.giftCard?.name || this.$t('general.giftcard')
    },
    info () {
      return this.$site.tabs.getInfo('giftCard')
    },
    homeRoute () {
      return this.$store.getters.homeRoute || { path: '/' }
    },
    breadcrumbs () {
      return [
        { text: (this.$site?.tabs?.giftCard?.name || this.$t('general.giftcard')), to: { path: '/gift' } },
        { text: this.$t('pages.gift.card.index.locationGiftCard', { location: this.$site.location.title }), active: true }
      ]
    },
    amounts () {
      return this.$site.tabs.giftCard.amounts
    },
    maxCustomAmount () {
      return this.$site.tabs.giftCard.maxCustomAmount
    },
    images () {
      return this.$site.tabs.giftCard.images
    },
    giftCardImage () {
      if (this.details.image) {
        return this.details.image.images.large
      } else if (this.$site.images.giftCard) {
        return this.$site.images.giftCard.large
      }
      return null
    },
    giftCardExpiryDateLabel () {
      const days = this.$site.tabs.giftCard.expiryDays
      if (!days) {
        return this.$t('general.neverExpires')
      }
      const today = new Date()

      const expiry = new Date()
      expiry.setDate(today.getDate() + days)

      return this.$format.date(expiry / 1000)
    }
  },
  created () {
    if (this.images && this.images.length > 0) {
      this.details.image = this.images[0]
    }
  },
  methods: {
    setImage (image) {
      this.details.image = image
    },
    setAmount (amount) {
      this.details.amount = amount
    },
    currencyAmount (amount) {
      if (!amount) { return (0).toLocaleString() }

      return parseFloat(amount).toLocaleString()
    },
    async next () {
      this.errors = []

      if (!this.details.amount || this.details.amount <= 0) { this.errors.push(this.$t('pages.gift.card.index.amountError')) }
      if (this._customAmount && this.details.amount && this.maxCustomAmount && Number(this.details.amount) > Number(this.maxCustomAmount)) {
        this.errors.push(this.$t('pages.gift.card.index.maxAmountError', { max: this.$site.location.currencySymbol + this.maxCustomAmount }))
      }
      if (!this.details.toName) { this.errors.push(this.$t('pages.gift.card.index.toNameError')) }
      if (!this.details.toEmail) { this.errors.push(this.$t('pages.gift.card.index.toEmailError')) }
      if (!this.details.fromName) { this.errors.push(this.$t('pages.gift.card.index.fromNameError')) }
      if (!this.details.message) { this.errors.push(this.$t('pages.gift.card.index.messageError')) }

      if (!Utils.validation.email(this.details.toEmail)) { this.errors.push(this.$t('pages.gift.card.index.validEmailError')) }

      if (this.errors.length) {
        this.$nextTick(() => {
          this.$refs?.errors?.$el?.scrollIntoView({ behavior: 'smooth' })
        })
        return
      }

      await this.$store.dispatch('gift/setCardDetails', { amount: this.details.amount, image: (this.details.image) ? this.details.image.id : null, toName: this.details.toName, toEmail: this.details.toEmail, fromName: this.details.fromName, message: this.details.message, deliveryDate: this.details.deliveryDate })

      const urlQuery = this.$store.getters['gift/urlQuery']
      this.$router.push({ path: '/gift/card/customer', query: urlQuery })
    }
  }
})
</script>
